/** Facebook workspace. Two modes (deals / contractors), three tools:
 *   Draft  — write a post, comment, DM or listing from a lead or from scratch
 *   Triage — paste posts you scrolled past, get keep/skip + reasons + drafts
 *   Rules  — teach it what you're looking for and what to answer
 *
 * No Facebook login, no posting, no Meta API — see ./api.ts. Everything here
 * ends with Tom copying text and doing it himself, on purpose. */
import { useEffect, useMemo, useState } from "react";
import type { Lead } from "../../types";
import {
  ACTION_ORDER, FB_CAPS, capState, dayStatus, findDuplicate, groupWarning,
  loadEntries, paceWarning, saveEntries, type FbAction, type FbEntry,
} from "./api";
import {
  DEFAULT_RULES, automationWithRules, narrowRules, rulesPrompt,
  type FbMode, type FbRules, type ModeRules,
} from "./rules";
import {
  buildTriageMessages, mergeTriage, parseVerdicts, ruleCheck, sortTriaged, splitPosts,
  type RuleCheck, type Triaged,
} from "./triage";
import {
  COPY_KINDS, DEFAULT_BASE, LocalAiError, chat, discover, generate, leadContext,
  loadConfig, normalizeBase, pickModel, saveConfig,
  type CopyKind, type Draft, type LocalAiConfig, type LocalAiStatus,
} from "../localai/api";
import type { AddPropertyForm } from "../leads/api";

const KINDS: CopyKind[] = ["comment", "dm", "group_post", "listing"];
const KIND_ACTION: Record<CopyKind, FbAction> = { comment: "comment", dm: "dm", group_post: "group_post", listing: "timeline_post" };
type Tool = "draft" | "triage" | "rules";

export function FacebookPage({ leads, automation, onSaveAutomation, onAddProperty, onOpenLead }: {
  leads: Lead[];
  automation: unknown;
  /** whole AUTOMATION object, merged — settings writes replace the key */
  onSaveAutomation?: ((value: Record<string, unknown>) => void) | undefined;
  onAddProperty?: ((payload: { form: AddPropertyForm; zillowUrl: string | null }) => void) | undefined;
  onOpenLead?: ((address: string) => void) | undefined;
}) {
  const [mode, setMode] = useState<FbMode>("deals");
  const [tool, setTool] = useState<Tool>("draft");
  const [entries, setEntries] = useState<FbEntry[]>(() => loadEntries());
  const [rules, setRules] = useState<FbRules>(() => narrowRules(automation));
  const [cfg, setCfg] = useState<LocalAiConfig>(() => loadConfig());
  const [status, setStatus] = useState<LocalAiStatus>({ state: "unknown", runtime: "", models: [], detail: "" });
  const [baseEdit, setBaseEdit] = useState(cfg.base);

  useEffect(() => { setRules(narrowRules(automation)); }, [automation]);

  const mr = rules[mode];
  const day = useMemo(() => dayStatus(entries, Date.now()), [entries]);

  const check = async (base?: string) => {
    const next = { ...cfg, base: normalizeBase(base ?? cfg.base) };
    setStatus({ state: "unknown", runtime: "", models: [], detail: "" });
    const st = await discover(next);
    setStatus(st);
    if (st.state === "on") {
      const merged = { base: st.detail || next.base, model: pickModel(st.models, next.model) };
      setCfg(merged); setBaseEdit(merged.base); saveConfig(merged);
    }
  };
  useEffect(() => { void check(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const log = (action: FbAction, target: string, text: string) => {
    const next = [{ ts: Date.now(), action, target, text }, ...entries];
    setEntries(next); saveEntries(next, Date.now());
  };
  const saveRules = (next: FbRules) => {
    setRules(next);
    onSaveAutomation?.(automationWithRules(automation, next));
  };

  return (
    <div className="fbpage">
      <div className="fbhead">
        <div>
          <h2 className="fbtitle">Facebook</h2>
          <div className="sub">Drafts on your Mac · you post by hand · nothing here logs into Facebook</div>
        </div>
        <div className="fbconn">
          <span className={"laipill " + (status.state === "on" ? "pill-emerald" : "pill-amber")}>
            {status.state === "on" ? `Local AI · ${cfg.model}` : status.state === "off" ? "Local AI off" : "checking…"}
          </span>
          <button className="btn slim linkish" onClick={() => void check()}>Re-check</button>
        </div>
      </div>

      <div className="fbmodes">
        <button className={"fbmode" + (mode === "deals" ? " on" : "")} onClick={() => setMode("deals")}>
          🏠 Finding deals<span className="fbmodesub">sellers, wholesalers, FSBO</span>
        </button>
        <button className={"fbmode" + (mode === "contractors" ? " on" : "")} onClick={() => setMode("contractors")}>
          🔨 Finding contractors<span className="fbmodesub">trades to hire</span>
        </button>
      </div>

      <div className="fbcaps">
        {ACTION_ORDER.map((a) => {
          const n = day.counts[a]; const cap = FB_CAPS[a].cap; const st = capState(n, cap);
          return (
            <div key={a} className={"fbcap " + st} title={FB_CAPS[a].note}>
              <div className="fbcapn">{n}<span className="fbcapof">/{cap}</span></div>
              <div className="fbcapl">{FB_CAPS[a].label}</div>
              <button className="btn xs fblogbtn" onClick={() => log(a, "", "(logged by hand)")}>+1</button>
            </div>
          );
        })}
      </div>
      {day.worst === "over" ? (
        <div className="lainote err">Over the day's limit on {day.overs.map((a) => FB_CAPS[a].label.toLowerCase()).join(", ")}. Stop there — this is the pattern that cost you 24 hours.</div>
      ) : day.worst === "near" ? <div className="lainote">Getting close to a daily limit. Spread the rest over the evening.</div> : null}

      {status.state === "off" ? (
        <div className="lainote">
          <b>No local model running.</b> Open LM Studio → <b>Local Model API</b> → server on, CORS on, then Re-check.
          <div className="lairow">
            <input className="laiin" value={baseEdit} placeholder={DEFAULT_BASE} onChange={(e) => setBaseEdit(e.target.value)} />
            <button className="btn slim" onClick={() => void check(baseEdit)}>Use this address</button>
          </div>
        </div>
      ) : null}

      <div className="fbtools">
        {(["draft", "triage", "rules"] as Tool[]).map((t) => (
          <button key={t} className={"btn slim" + (tool === t ? " primary" : "")} onClick={() => setTool(t)}>
            {t === "draft" ? "Draft" : t === "triage" ? "Triage posts" : "Rules"}
          </button>
        ))}
      </div>

      {tool === "draft" ? (
        <DraftPanel mode={mode} mr={mr} leads={leads} cfg={cfg} ready={status.state === "on"}
          entries={entries} onLog={log} onOpenLead={onOpenLead} />
      ) : tool === "triage" ? (
        <TriagePanel mode={mode} mr={mr} cfg={cfg} ready={status.state === "on"}
          entries={entries} onLog={log} onAddProperty={onAddProperty} />
      ) : (
        <RulesPanel mode={mode} rules={rules} onSave={saveRules} canSave={!!onSaveAutomation} />
      )}

      <div className="fbsec">
        <div className="sechead static"><span>Activity <span className="dim">· last 15</span></span></div>
        {entries.length === 0 ? (
          <div className="sub">Nothing logged yet. “Log it” on a draft, or “+1” on a counter for anything you did straight in Facebook.</div>
        ) : (
          <table className="fbtable"><tbody>
            {entries.slice(0, 15).map((e, i) => (
              <tr key={i}>
                <td className="fbtime">{new Date(e.ts).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                <td className="fbact">{FB_CAPS[e.action].label}</td>
                <td className="fbtgt">{e.target || "—"}</td>
                <td className="fbtxt">{e.text.slice(0, 80)}{e.text.length > 80 ? "…" : ""}</td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ draft */

function DraftPanel({ mode, mr, leads, cfg, ready, entries, onLog, onOpenLead }: {
  mode: FbMode; mr: ModeRules; leads: Lead[]; cfg: LocalAiConfig; ready: boolean;
  entries: FbEntry[]; onLog: (a: FbAction, t: string, x: string) => void; onOpenLead?: ((a: string) => void) | undefined;
}) {
  const [kind, setKind] = useState<CopyKind>("comment");
  const [count, setCount] = useState(5);
  const [target, setTarget] = useState("");
  const [city, setCity] = useState("");
  const [detail, setDetail] = useState("");
  const [leadPick, setLeadPick] = useState("");
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [busy, setBusy] = useState(false);
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<number | null>(null);

  useEffect(() => { if (!busy) return; setSecs(0); const t = setInterval(() => setSecs((n) => n + 1), 1000); return () => clearInterval(t); }, [busy]);

  const lead = leads.find((l) => l.address === leadPick) ?? null;
  const context = lead
    ? leadContext(lead, undefined)
    : [city && "City/area: " + city, detail && "Detail: " + detail, target && "About: " + target].filter(Boolean).join("\n");

  const pace = paceWarning(entries, KIND_ACTION[kind], Date.now());
  const gw = kind === "group_post" ? groupWarning(entries, target, Date.now()) : null;

  const run = async () => {
    if (!context.trim()) { setErr("Give it something real — a city, a detail, or pick a lead."); return; }
    setBusy(true); setErr(null); setDrafts([]);
    try {
      setDrafts(await generate(cfg, kind, `${rulesPrompt(mode, mr)}\n\n${context}`, count));
    } catch (e) {
      setErr(e instanceof LocalAiError ? e.message : (e as Error)?.message ?? "Couldn't reach the local model.");
    } finally { setBusy(false); }
  };
  const copy = (i: number, text: string) => {
    void navigator.clipboard?.writeText(text).catch(() => undefined);
    setCopied(i); setTimeout(() => setCopied((c) => (c === i ? null : c)), 1500);
  };

  return (
    <div className="fbsec">
      <div className="sechead static"><span>Draft something</span></div>
      <div className="laikinds">
        {KINDS.map((k) => <button key={k} className={"btn slim" + (kind === k ? " primary" : "")} onClick={() => setKind(k)}>{COPY_KINDS[k].label}</button>)}
        <select className="laisel" value={count} onChange={(e) => setCount(Number(e.target.value))}>
          {[3, 5, 8].map((n) => <option key={n} value={n}>{n} versions</option>)}
        </select>
        <button className="btn" disabled={busy || !ready} onClick={() => void run()}>{busy ? `Drafting… ${secs}s` : "Draft"}</button>
      </div>

      <div className="fbfields">
        <label className="fbf"><span>{kind === "group_post" ? "Which group" : "Where / who"}</span>
          <input className="laiin" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={kind === "group_post" ? "Central FL Investors" : "their post"} /></label>
        <label className="fbf"><span>City / area</span>
          <input className="laiin" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ocala" /></label>
        <label className="fbf"><span>The detail that makes it real</span>
          <input className="laiin" value={detail} onChange={(e) => setDetail(e.target.value)} placeholder={mode === "deals" ? "roof from 2009" : "the tile job in their photos"} /></label>
        {mode === "deals" ? (
          <label className="fbf"><span>…or pull it from a lead</span>
            <select className="laisel" value={leadPick} onChange={(e) => setLeadPick(e.target.value)}>
              <option value="">— type it myself —</option>
              {leads.slice(0, 400).map((l) => <option key={l.address} value={l.address}>{l.address}</option>)}
            </select></label>
        ) : null}
      </div>

      {lead && onOpenLead ? <div className="sub">Using {lead.address} · <button className="btn slim linkish" onClick={() => onOpenLead(lead.address)}>open lead</button></div> : null}
      {pace ? <div className="lainote">{pace}</div> : null}
      {gw ? <div className="lainote err">{gw}</div> : null}
      {err ? <div className="lainote err">{err}</div> : null}
      {busy && secs > 25 ? <div className="sub">Still going — local models think for a while. 20–90 seconds is normal.</div> : null}

      {drafts.map((d, i) => {
        const dup = findDuplicate(entries, d.text, Date.now());
        return (
          <div className="laicard" key={i}>
            <button className="btn slim laicopy" onClick={() => copy(i, d.text)}>{copied === i ? "Copied" : "Copy"}</button>
            <div className="laitext">{d.text}</div>
            <div className="laiflags">
              {d.flags.length ? d.flags.map((f, j) => <span key={j} className="pill-amber laipill">{f}</span>)
                : <span className="pill-emerald laipill">clean · {d.words} words</span>}
              {dup ? <span className="laipill pill-amber">you sent something like this {new Date(dup.ts).toLocaleDateString()}</span> : null}
              <button className="btn xs" onClick={() => onLog(KIND_ACTION[kind], target || city, d.text)}>Log it</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ----------------------------------------------------------------- triage */

function TriagePanel({ mode, mr, cfg, ready, entries, onLog, onAddProperty }: {
  mode: FbMode; mr: ModeRules; cfg: LocalAiConfig; ready: boolean;
  entries: FbEntry[]; onLog: (a: FbAction, t: string, x: string) => void;
  onAddProperty?: ((p: { form: AddPropertyForm; zillowUrl: string | null }) => void) | undefined;
}) {
  const [blob, setBlob] = useState("");
  const [rows, setRows] = useState<Triaged[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<number, Draft[]>>({});
  const [drafting, setDrafting] = useState<number | null>(null);
  const [added, setAdded] = useState<Record<number, boolean>>({});

  useEffect(() => { if (!busy) return; setSecs(0); const t = setInterval(() => setSecs((n) => n + 1), 1000); return () => clearInterval(t); }, [busy]);

  const run = async () => {
    const posts = splitPosts(blob);
    if (!posts.length) { setErr("Paste a few posts first — separate them with a blank line."); return; }
    setBusy(true); setErr(null); setRows(null); setDrafts({});
    const checks: RuleCheck[] = posts.map((p) => ruleCheck(p.text, mode, mr));
    const undecided = posts.filter((_, i) => checks[i]!.verdict === "unsure");
    try {
      let verdicts: Partial<Triaged>[] = [];
      if (undecided.length && ready) {
        const out = await chat(cfg, buildTriageMessages(undecided, mode, mr), 0.2);
        verdicts = parseVerdicts(out);
      }
      setRows(sortTriaged(mergeTriage(posts, checks, verdicts, mode)));
    } catch (e) {
      setErr(e instanceof LocalAiError ? e.message : (e as Error)?.message ?? "Triage failed.");
      setRows(sortTriaged(mergeTriage(posts, checks, [], mode)));
    } finally { setBusy(false); }
  };

  const draftFor = async (row: Triaged, kind: CopyKind) => {
    setDrafting(row.id);
    try {
      const ctx = `${rulesPrompt(mode, mr)}\n\nTHEIR POST:\n${row.text.slice(0, 700)}`;
      setDrafts((d) => ({ ...d, [row.id]: [] }));
      const out = await generate(cfg, kind, ctx, 3);
      setDrafts((d) => ({ ...d, [row.id]: out }));
    } catch (e) {
      setErr(e instanceof LocalAiError ? e.message : (e as Error)?.message ?? "Draft failed.");
    } finally { setDrafting(null); }
  };

  const convert = (row: Triaged) => {
    if (!onAddProperty || !row.address) return;
    const form = {
      address: row.address, city: row.city ?? "", county: "", state: "FL", zip: "",
      ptype: "", beds: "", baths: "", sqft: "", year: "", lot: "",
      price: row.price ? String(row.price) : "", arv: "", rehab: "",
      status: "Needs Review", source: "Facebook",
    } as AddPropertyForm;
    onAddProperty({ form, zillowUrl: null });
    setAdded((a) => ({ ...a, [row.id]: true }));
  };

  return (
    <div className="fbsec">
      <div className="sechead static"><span>Triage posts <span className="dim">· paste what you scrolled past</span></span></div>
      <textarea className="fbpaste" value={blob} onChange={(e) => setBlob(e.target.value)} rows={7}
        placeholder={"Paste posts here, one per paragraph — a blank line between them.\n\nInherited house in Ocala, needs a roof, asking 180k as-is\n\n3/2 for rent in Belleview $1,800/mo"} />
      <div className="laikinds">
        <button className="btn" disabled={busy} onClick={() => void run()}>{busy ? `Reading… ${secs}s` : "Triage"}</button>
        <span className="sub">{splitPosts(blob).length} post(s) detected · your rules run first, the model only sees what they can't decide</span>
      </div>
      {err ? <div className="lainote err">{err}</div> : null}

      {rows?.map((row) => (
        <div className={"fbrow " + row.verdict} key={row.id}>
          <div className="fbrowhead">
            <span className={"laipill " + (row.verdict === "keep" ? "pill-emerald" : row.verdict === "skip" ? "pill-amber" : "pill-blue")}>
              {row.verdict === "keep" ? "KEEP" : row.verdict === "skip" ? "SKIP" : "UNSURE"}
            </span>
            <span className="fbreason">{row.reason}{row.byRule ? " · your rule, no model needed" : ""}</span>
          </div>
          <div className="fbrowtext">{row.text.slice(0, 320)}{row.text.length > 320 ? "…" : ""}</div>
          {(row.address || row.city || row.price || row.trade || row.name) ? (
            <div className="sub">
              {[row.address, row.city, row.price ? `$${row.price.toLocaleString("en-US")}` : "", row.trade, row.name].filter(Boolean).join(" · ")}
            </div>
          ) : null}
          {row.verdict !== "skip" ? (
            <div className="laiflags">
              <button className="btn xs" disabled={!ready || drafting === row.id} onClick={() => void draftFor(row, "comment")}>
                {drafting === row.id ? "…" : "Draft comment"}
              </button>
              <button className="btn xs" disabled={!ready || drafting === row.id} onClick={() => void draftFor(row, "dm")}>Draft DM</button>
              {mode === "deals" && onAddProperty && row.address ? (
                <button className="btn xs" disabled={added[row.id]} onClick={() => convert(row)}>
                  {added[row.id] ? "Added to Properties ✓" : "Convert to lead"}
                </button>
              ) : null}
            </div>
          ) : null}
          {(drafts[row.id] ?? []).map((d, i) => (
            <div className="laicard" key={i}>
              <button className="btn slim laicopy" onClick={() => void navigator.clipboard?.writeText(d.text).catch(() => undefined)}>Copy</button>
              <div className="laitext">{d.text}</div>
              <div className="laiflags">
                {d.flags.length ? d.flags.map((f, j) => <span key={j} className="pill-amber laipill">{f}</span>)
                  : <span className="pill-emerald laipill">clean · {d.words} words</span>}
                <button className="btn xs" onClick={() => onLog("comment", row.city ?? row.address ?? "", d.text)}>Log it</button>
              </div>
            </div>
          ))}
        </div>
      ))}
      {rows && !rows.length ? <div className="sub">Nothing to read there.</div> : null}
      {entries.length && rows ? null : null}
    </div>
  );
}

/* ------------------------------------------------------------------ rules */

function RulesPanel({ mode, rules, onSave, canSave }: {
  mode: FbMode; rules: FbRules; onSave: (r: FbRules) => void; canSave: boolean;
}) {
  const [draft, setDraft] = useState<ModeRules>(rules[mode]);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setDraft(rules[mode]); setSaved(false); }, [mode, rules]);

  const set = <K extends keyof ModeRules>(k: K, v: ModeRules[K]) => { setDraft((d) => ({ ...d, [k]: v })); setSaved(false); };
  const csv = (a: string[]) => a.join(", ");
  const parseCsv = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const save = () => { onSave({ ...rules, [mode]: draft }); setSaved(true); };

  return (
    <div className="fbsec">
      <div className="sechead static">
        <span>Rules for {mode === "deals" ? "finding deals" : "finding contractors"}</span>
        <span>
          {saved ? <span className="laipill pill-emerald">saved</span> : null}
          <button className="btn slim" style={{ marginLeft: 8 }} onClick={save} disabled={!canSave}>Save rules</button>
        </span>
      </div>
      {!canSave ? <div className="lainote">Saving is off on this account — rules will apply for this session only.</div> : null}

      <label className="fbf"><span>What a good one looks like</span>
        <textarea className="laiin" rows={4} value={draft.looksLike} onChange={(e) => set("looksLike", e.target.value)} /></label>
      <label className="fbf"><span>What to skip</span>
        <textarea className="laiin" rows={3} value={draft.skip} onChange={(e) => set("skip", e.target.value)} /></label>

      <div className="fbfields">
        <label className="fbf"><span>Good words (any one = worth a look)</span>
          <input className="laiin" value={csv(draft.keywords)} onChange={(e) => set("keywords", parseCsv(e.target.value))} /></label>
        <label className="fbf"><span>Deal-breaker words (instant skip)</span>
          <input className="laiin" value={csv(draft.blockers)} onChange={(e) => set("blockers", parseCsv(e.target.value))} /></label>
        <label className="fbf"><span>Areas you {mode === "deals" ? "buy" : "hire"} in</span>
          <input className="laiin" value={csv(draft.areas)} onChange={(e) => set("areas", parseCsv(e.target.value))} /></label>
        {mode === "deals" ? (
          <>
            <label className="fbf"><span>Most you'd pay</span>
              <input className="laiin" value={draft.maxPrice ? String(draft.maxPrice) : ""} onChange={(e) => set("maxPrice", Number(e.target.value.replace(/[^0-9]/g, "")) || 0)} /></label>
            <label className="fbf"><span>Ignore under</span>
              <input className="laiin" value={draft.minPrice ? String(draft.minPrice) : ""} onChange={(e) => set("minPrice", Number(e.target.value.replace(/[^0-9]/g, "")) || 0)} /></label>
          </>
        ) : null}
        <label className="fbf"><span>Follow up after (days)</span>
          <input className="laiin" value={String(draft.followUpDays)} onChange={(e) => set("followUpDays", Math.max(1, Number(e.target.value.replace(/[^0-9]/g, "")) || 1))} /></label>
      </div>

      <label className="fbf"><span>How you want to sound</span>
        <textarea className="laiin" rows={2} value={draft.voice} onChange={(e) => set("voice", e.target.value)} /></label>

      <div className="sechead static"><span>Answers to the questions that keep coming up</span></div>
      {draft.answers.map((qa, i) => (
        <div className="fbqa" key={i}>
          <input className="laiin" value={qa.q} placeholder="they ask…"
            onChange={(e) => set("answers", draft.answers.map((x, j) => (j === i ? { ...x, q: e.target.value } : x)))} />
          <textarea className="laiin" rows={2} value={qa.a} placeholder="you answer…"
            onChange={(e) => set("answers", draft.answers.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)))} />
          <button className="btn xs danger" onClick={() => set("answers", draft.answers.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      <div className="laikinds">
        <button className="btn slim" onClick={() => set("answers", [...draft.answers, { q: "", a: "" }])}>+ Add answer</button>
        <button className="btn slim" onClick={() => { setDraft(DEFAULT_RULES[mode]); setSaved(false); }}>Reset to defaults</button>
      </div>
    </div>
  );
}
